import { Card } from '@/components/shadcn-ui/card';
import Loader from '@/components/sharedComponents/Loader';
import AddStorageLotDrawser from '@/components/storage-stage-components/AddStorageLotDrawser';
import ConsumptionCard from '@/components/storage-stage-components/ConsumptionCard';
import RecordConsumptionDrawer from '@/components/storage-stage-components/RecordConsumptionDrawer';
import StorageLotDetailCard from '@/components/storage-stage-components/StorageLotDetailCard';
import { useLotStore } from '@/stores/useLot.store';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

export const formatReadableDate = isoString => {
  const date = new Date(isoString);

  const day = date.getDate();

  // Add st, nd, rd, th
  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
          ? 'rd'
          : 'th';

  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();

  return `${day}${suffix} ${month} ${year}`;
};

const StorageLotDetailPage = () => {
  const [loading, setLoading] = useState(false);

  const { lot_id } = useParams();
  const fetchLot = useLotStore(state => state.fetchLot);
  const lot = useLotStore(state => state.lot);

  const fetchStorageDetailLots = useLotStore(state => state.fetchStorageDetailLots);
  const storageDetailLots = useLotStore(state => state.storageDetailLots);

  // Storage Consumptions
  const fetchStorageConsumptions = useLotStore(state => state.fetchStorageConsumptions);
  const storageConsumptions = useLotStore(state => state.storageConsumptions);

  const fetchLotData = async () => {
    await fetchLot(lot_id, false);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchLotData();
        await fetchStorageDetailLots(lot_id);
        await fetchStorageConsumptions(lot_id);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [lot_id]);

  const totalWeight = useMemo(() => {
    return storageDetailLots.reduce((acc, lot) => acc + lot.total_weight, 0);
  }, [storageDetailLots]);

  const totalBags = useMemo(() => {
    return storageDetailLots.reduce((acc, lot) => acc + lot.bags, 0);
  }, [storageDetailLots]);

  // const consumptionWeight = useMemo(() => {
  //   return storageConsumptions.reduce((acc, consumption) => acc + Number(consumption.consumption_weight || 0), 0);
  // }, [storageConsumptions]);

  const consumptionWeight = useMemo(() => {
    return storageDetailLots.reduce((acc, consumption) => {
      // console.log('Consumption at iteration:', consumption);
      return acc + Number(consumption.consumption_weight || 0);
    }, 0);
  }, [storageDetailLots]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-1 flex-col space-y-[18px] px-4 py-[18px] pb-2">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
        <Card className="flex flex-col gap-1 overflow-hidden p-4">
          <div className="flex items-center justify-between">
            <div className="mb-1 text-base font-semibold">Lot Information</div>
            {/* <button className="mr-1" onClick={handleGenerateAllBedPdfs}>
            <Printer size={20} />
          </button> */}
          </div>
          <div className="text-sm">
            <span className="font-medium">Lot:</span> {lot?.lotname}
          </div>
          <div className="text-sm">
            <span className="font-medium">Estate:</span> {lot?.estate?.name}
          </div>
          <div className="text-sm">
            <span className="font-medium">Varietal:</span> {lot?.varietal}
          </div>
          <div className="text-sm">
            <span className="font-medium">Drying end date:</span>{' '}
            {formatReadableDate(lot?.drying_lot?.drying_end_time || new Date())}
          </div>
        </Card>

        {storageDetailLots.length > 0 && (
          <Card className="flex flex-col gap-1 overflow-hidden p-4">
            <div className="flex items-center justify-between">
              <div className="mb-1 text-base font-semibold">Storage inventory</div>
            </div>
            <div className="text-sm">
              <span className="font-medium">Total weight:</span> {totalWeight.toFixed(2)}kg
            </div>
            <div className="text-sm">
              <span className="font-medium">Total bags:</span> {totalBags}
            </div>
            <div className="text-sm">
              <span className="font-medium">Available weight:</span>
              {/* {totalWeight - consumptionWeight}kg */}
              <span>{(Number(totalWeight || 0) - Number(consumptionWeight || 0)).toFixed(2)} kg</span>
            </div>
          </Card>
        )}

        <div className="space-y-4">
          {/* Storage Lots */}
          {storageDetailLots.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="font-medium">Storage Lots</div>
              <div className="flex flex-col gap-2">
                {storageDetailLots.map((lot, i) => (
                  <StorageLotDetailCard
                    key={i}
                    lot={lot}
                    idx={i}
                    fetchStorageDetailLots={() => fetchStorageDetailLots(lot_id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Consumption */}
          {storageConsumptions.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="font-medium">Consumption</div>
              <div className="flex flex-col gap-2">
                {storageConsumptions.map((consumption, i) => (
                  <ConsumptionCard
                    key={i}
                    idx={i}
                    consumption={consumption}
                    fetchStorageConsumptions={() => fetchStorageConsumptions(lot_id)}
                    storageDetailLots={storageDetailLots}
                    fetchStorageDetailLots={() => fetchStorageDetailLots(lot_id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <AddStorageLotDrawser
          stage={lot?.stage}
          lot={lot}
          fetchStorageDetailLots={() => fetchStorageDetailLots(lot_id)}
        />
        {storageDetailLots.length > 0 && (
          <RecordConsumptionDrawer
            stage={lot?.stage}
            fetchStorageDetailLots={() => fetchStorageDetailLots(lot_id)}
            storageDetailLots={storageDetailLots}
            fetchStorageConsumptions={() => fetchStorageConsumptions(lot_id)}
          />
        )}
      </div>
    </div>
  );
};

export default StorageLotDetailPage;
